<script lang="ts" setup>
import { computed, ref, watch, onMounted, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';

import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';

import { AppSvgIcon } from '@libs/ui';
import type { AppSvgIconSize } from '@libs/ui';

import { useHotelAmenitiesStore } from '../../core/stores';

interface HotelAmenityDataWithSize extends HotelAmenityData {
  size: number;
}

interface HotelAmenitiesProps {
  amenities: HotelAmenityData[][];
}

const props = defineProps<HotelAmenitiesProps>();

const amenitiesAmenitiesRef = useTemplateRef('amenitiesAmenitiesRef');
const amenitiesWithSizes = ref<HotelAmenityDataWithSize[]>([]);
const { width, height } = useElementSize(amenitiesAmenitiesRef);
const hotelAmenitiesStore = useHotelAmenitiesStore();
watch(props, () => {
  updateAmenitiesWithSizes();
});

onMounted(() => {
  updateAmenitiesWithSizes();
});

const iconSize = 18;
const iconAmenitySpacing = 8;
const amenityAmenityXSpacing = 16;
const amenityAmenityYSpacing = 8;

const mobileAmenities = computed<HotelAmenityDataWithSize[][]>(() => {
  const rows: HotelAmenityDataWithSize[][] = [];
  let currentRow: HotelAmenityDataWithSize[] = [];
  let currentWidth = 0;

  const minRowsAmount = 1;

  const maxRowWidth = width.value;
  const maxRowsAmount =
    Math.floor(height.value / (iconSize + amenityAmenityYSpacing)) ||
    minRowsAmount;

  amenitiesWithSizes.value.forEach((amenity) => {
    if (currentWidth + amenity.size > maxRowWidth && currentRow.length > 0) {
      rows.push(currentRow);
      currentRow = [];
      currentWidth = 0;
    }
    currentRow.push(amenity);
    currentWidth += amenity.size;
  });

  if (currentRow.length > 0) {
    rows.push(currentRow);
  }
  if (rows.length > maxRowsAmount) {
    return rows.slice(0, maxRowsAmount);
  }

  return rows;
});

function updateAmenitiesWithSizes(): void {
  amenitiesWithSizes.value = props.amenities
    .flat()
    .map((amenity: HotelAmenityData) => ({
      ...amenity,
      size: hotelAmenitiesStore.amenitiesWithSizes[amenity.amenityType] ?? 0,
    }));
}
</script>

<template>
  <div
    ref="amenitiesAmenitiesRef"
    class="hotel-amenities"
    :style="{
      '--icon-text-gap': iconAmenitySpacing + 'px',
      '--amenities-x-gap': amenityAmenityXSpacing + 'px',
      '--amenities-y-gap': amenityAmenityYSpacing + 'px',
    }"
  >
    <div
      v-for="(row, rowIndex) in mobileAmenities"
      :key="rowIndex"
      class="amenity-row"
    >
      <div v-for="amenity in row" :key="amenity.value" class="amenity">
        <AppSvgIcon
          :name="amenity.icon"
          :size="iconSize.toString() as AppSvgIconSize"
          color="color-neutral-60"
        />
        <span class="amenity-text">{{ amenity.value }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hotel-amenities {
  display: flex;
  flex-direction: column;
  gap: var(--amenities-y-gap);
  height: 100%;

  .amenity-row {
    display: flex;
    flex-wrap: nowrap;
    gap: var(--amenities-x-gap);
  }

  .amenity {
    display: flex;
    align-items: center;
    gap: var(--icon-text-gap);

    .amenity-text {
      white-space: nowrap;
      color: var(--color-neutral-60);
      @include font-body-s-light;
    }
  }
}
</style>
